import { useEffect, useRef } from 'react';
import { db, ensureAuth, auth } from './firebase';
import { collection, addDoc, doc, getDocs, limit, orderBy, query, Timestamp, updateDoc, where } from 'firebase/firestore';
import { PortalActivityType, User } from '../types';
import { isPortalStaffUser } from './userRoles';

const SESSION_ID = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const HEARTBEAT_INTERVAL_MS = 60_000;
/** Pause the engagement timer after this much input inactivity. Prevents tabs left open all night from inflating "Active" time. */
const IDLE_THRESHOLD_MS = 5 * 60_000;
/** Hard cap — stop heartbeats and close the session so one forgotten tab cannot run for hours. */
const MAX_ENGAGEMENT_SESSION_MS = 2 * 60 * 60_000;

export type EngagementPage =
  | 'portal'
  | 'proposal'
  | 'change_order'
  | 'portal_pdf'
  | 'portal_3d_model'
  | 'portal_inspiration_board';

export interface EngagementSessionOptions {
  portalId: string | null | undefined;
  projectId?: string | null;
  source: 'client' | 'employee' | 'stakeholder';
  page: EngagementPage;
  openedAction: string;
  closedAction: string;
  enabled?: boolean;
  extraMetadata?: Record<string, unknown>;
}

/** Set by ClientPortalSplash when the viewer is a stakeholder so all portalActivity rows include their display name. */
let stakeholderDisplayNameContext: string | null = null;
/** Optional CRM lead id when the stakeholder link was created from a lead invite. */
let stakeholderLeadIdContext: string | null = null;
/** Stakeholder magic-link doc id — ties portalActivity rows to a specific invite. */
let stakeholderLinkIdContext: string | null = null;

export function setPortalActivityStakeholderDisplayName(name: string | null) {
  stakeholderDisplayNameContext = name?.trim() || null;
}

export function setPortalActivityStakeholderLeadId(leadId: string | null) {
  stakeholderLeadIdContext = leadId?.trim() || null;
}

export function setPortalActivityStakeholderLinkId(linkId: string | null) {
  stakeholderLinkIdContext = linkId?.trim() || null;
}

/**
 * Classify portal activity as client vs internal staff vs stakeholder.
 * Any signed-in (non-anonymous) Firebase user is treated as staff so CRM
 * previews never pollute the client timeline when the User doc is still loading.
 */
export function resolvePortalActivitySource(args: {
  user: User | null | undefined;
  isStakeholderViewer: boolean;
}): 'client' | 'employee' | 'stakeholder' {
  if (args.isStakeholderViewer) return 'stakeholder';
  if (args.user && isPortalStaffUser(args.user.role)) return 'employee';
  const fbUser = auth.currentUser;
  if (fbUser && !fbUser.isAnonymous) return 'employee';
  return 'client';
}

function mergeActivityMetadata(
  metadata: Record<string, any> | undefined,
  source: 'client' | 'employee' | 'stakeholder' | 'agent',
): Record<string, any> | null {
  const merged: Record<string, any> = metadata ? { ...metadata } : {};
  if (source === 'stakeholder') {
    const name = merged.stakeholderDisplayName ?? stakeholderDisplayNameContext;
    if (name) merged.stakeholderDisplayName = String(name).trim();
    const leadId = merged.leadId ?? stakeholderLeadIdContext;
    if (leadId) merged.leadId = String(leadId).trim();
    const linkId = merged.stakeholderLinkId ?? stakeholderLinkIdContext;
    if (linkId) merged.stakeholderLinkId = String(linkId).trim();
  }
  return Object.keys(merged).length > 0 ? merged : null;
}

export async function logPortalActivity(
  portalId: string,
  projectId: string | null | undefined,
  type: PortalActivityType,
  action: string,
  metadata?: Record<string, any>,
  source: 'client' | 'employee' | 'stakeholder' | 'agent' = 'client',
) {
  try {
    await ensureAuth();
    await addDoc(collection(db, 'portalActivity'), {
      portalId,
      projectId: projectId || null,
      sessionId: SESSION_ID,
      type,
      action,
      metadata: mergeActivityMetadata(metadata, source),
      userAgent: navigator.userAgent,
      source,
      timestamp: Timestamp.now(),
    });
  } catch (err) {
    console.warn('[PortalActivity] Failed to log:', action, err);
  }
}

/**
 * Log an AI-agent event into the portal activity feed (rendered with a
 * distinct icon + "Agent" tag and its own filter tab in the workspace).
 * Known actions: enrichment_completed, bidding_analysis_ready,
 * proposal_reviewed, document_synopsis_ready.
 */
export async function logAgentPortalActivity(
  portalId: string,
  projectId: string | null | undefined,
  action: string,
  metadata?: Record<string, any>,
) {
  await logPortalActivity(portalId, projectId, 'action', action, metadata, 'agent');
}

/**
 * Same as logAgentPortalActivity, but resolves the portal from the lead —
 * for agent surfaces (enrichment, bidding) that only know the leadId.
 * No-op when the lead has no portal yet.
 */
export async function logAgentActivityForLead(
  leadId: string,
  action: string,
  metadata?: Record<string, any>,
) {
  try {
    await ensureAuth();
    // Staff/CRM only — guests are denied clientPortals leadId list queries under PIN rules.
    const snap = await getDocs(
      query(collection(db, 'clientPortals'), where('leadId', '==', leadId), limit(1)),
    );
    const portalId = snap.docs[0]?.id;
    if (!portalId) return;
    await logAgentPortalActivity(portalId, null, action, metadata);
  } catch (err) {
    console.error(
      '[PortalActivity] Failed to log agent activity for lead (clientPortals leadId query):',
      action,
      leadId,
      err,
    );
  }
}

/** Log a client/staff proposal PDF download into the portal activity feed. */
export async function logProposalDownloadToPortal(options: {
  portalId: string;
  projectId?: string | null;
  source: 'client' | 'employee' | 'stakeholder';
  proposalSlug?: string;
  isSigned?: boolean;
  totalAmount?: number;
  projectName?: string;
  downloadId?: string;
}): Promise<void> {
  const { portalId, projectId, source, proposalSlug, isSigned, totalAmount, projectName, downloadId } =
    options;
  await logPortalActivity(
    portalId,
    projectId,
    'action',
    isSigned ? 'downloaded_signed_proposal' : 'downloaded_proposal',
    {
      proposalSlug,
      isSigned: !!isSigned,
      totalAmount,
      projectName,
      downloadId,
    },
    source,
  );
}

const AGREEMENT_SIGN_ACTIONS = new Set(['signed_agreement', 'agreement_signed']);

/**
 * When staff corrects `agreementSignedAt` on the portal, move the latest matching
 * portal activity row so the internal portal timeline stays aligned.
 */
export async function correctLatestAgreementPortalActivityTimestamp(
  portalId: string,
  projectId: string | null | undefined,
  newDate: Date,
): Promise<boolean> {
  try {
    await ensureAuth();
    const wantPid = projectId || null;
    const q = query(
      collection(db, 'portalActivity'),
      where('portalId', '==', portalId),
      orderBy('timestamp', 'desc'),
      limit(120),
    );
    const snap = await getDocs(q);
    for (const d of snap.docs) {
      const data = d.data();
      if (!AGREEMENT_SIGN_ACTIONS.has(String(data.action || ''))) continue;
      const docPid = data.projectId ?? null;
      if (docPid !== wantPid) continue;
      await updateDoc(doc(db, 'portalActivity', d.id), {
        timestamp: Timestamp.fromDate(newDate),
      });
      return true;
    }
  } catch (err) {
    console.warn('[PortalActivity] Failed to correct agreement timestamp:', err);
  }
  return false;
}

/**
 * Tracks active time on a portal surface (portal home, proposal, change order).
 * Logs open/close and periodic heartbeats with `metadata.page` + `secondsOnPage`.
 *
 * Only counts "active" time — pauses when the tab is hidden or the user is idle
 * (no input for IDLE_THRESHOLD_MS). Also logs close on unmount so SPA navigations
 * (e.g. portal → proposal) record duration instead of silently dropping it.
 */
export function useEngagementSession({
  portalId,
  projectId,
  source,
  page,
  openedAction,
  closedAction,
  enabled = true,
  extraMetadata,
}: EngagementSessionOptions) {
  const projectIdRef = useRef(projectId ?? null);
  projectIdRef.current = projectId ?? null;
  const extraMetadataRef = useRef(extraMetadata);
  extraMetadataRef.current = extraMetadata;

  const accumulatedRef = useRef(0);
  const activeStartRef = useRef<number | null>(null);
  const lastActivityRef = useRef(Date.now());
  const lastReportedSecondsRef = useRef(-1);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const closedRef = useRef(false);

  useEffect(() => {
    if (!portalId || !enabled) return;

    accumulatedRef.current = 0;
    activeStartRef.current = Date.now();
    lastActivityRef.current = Date.now();
    lastReportedSecondsRef.current = -1;
    closedRef.current = false;

    const sessionMetadata = (secondsOnPage?: number) => {
      const meta: Record<string, unknown> = {
        page,
        ...(extraMetadataRef.current || {}),
      };
      if (typeof secondsOnPage === 'number') {
        meta.secondsOnPage = secondsOnPage;
      }
      return meta;
    };

    const computeActiveSeconds = () => {
      let total = accumulatedRef.current;
      if (activeStartRef.current !== null) {
        total += Date.now() - activeStartRef.current;
      }
      return Math.round(total / 1000);
    };

    const pause = () => {
      if (activeStartRef.current !== null) {
        accumulatedRef.current += Date.now() - activeStartRef.current;
        activeStartRef.current = null;
      }
    };

    const resume = () => {
      if (activeStartRef.current === null) {
        activeStartRef.current = Date.now();
      }
      lastActivityRef.current = Date.now();
    };

    const currentProjectId = () => projectIdRef.current;

    logPortalActivity(portalId, currentProjectId(), 'page_view', openedAction, sessionMetadata(), source);

    intervalRef.current = setInterval(() => {
      if (
        activeStartRef.current !== null &&
        Date.now() - lastActivityRef.current > IDLE_THRESHOLD_MS
      ) {
        pause();
      }
      const elapsedMs =
        accumulatedRef.current +
        (activeStartRef.current !== null ? Date.now() - activeStartRef.current : 0);
      if (elapsedMs >= MAX_ENGAGEMENT_SESSION_MS) {
        pause();
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = null;
        logClose();
        return;
      }
      const elapsed = Math.round(elapsedMs / 1000);
      if (elapsed > 0 && elapsed !== lastReportedSecondsRef.current) {
        lastReportedSecondsRef.current = elapsed;
        logPortalActivity(
          portalId,
          currentProjectId(),
          'heartbeat',
          'heartbeat',
          sessionMetadata(elapsed),
          source,
        );
      }
    }, HEARTBEAT_INTERVAL_MS);

    const logClose = () => {
      if (closedRef.current) return;
      closedRef.current = true;
      pause();
      const elapsed = computeActiveSeconds();
      logPortalActivity(
        portalId,
        currentProjectId(),
        'page_view',
        closedAction,
        sessionMetadata(elapsed),
        source,
      );
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        // Pause the timer only — do not log "left portal". Tab switches were
        // producing false "Left portal · 99m" rows while heartbeats kept running.
        pause();
      } else if (document.visibilityState === 'visible') {
        lastActivityRef.current = Date.now();
        resume();
      }
    };

    const handleActivity = () => {
      if (document.visibilityState !== 'visible') return;
      if (activeStartRef.current === null) {
        activeStartRef.current = Date.now();
      }
      lastActivityRef.current = Date.now();
    };

    window.addEventListener('beforeunload', logClose);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('mousemove', handleActivity, { passive: true });
    window.addEventListener('keydown', handleActivity, { passive: true });
    window.addEventListener('scroll', handleActivity, { passive: true });
    window.addEventListener('touchstart', handleActivity, { passive: true });
    window.addEventListener('click', handleActivity, { passive: true });

    return () => {
      logClose();
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener('beforeunload', logClose);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('click', handleActivity);
    };
  }, [portalId, projectId, source, page, openedAction, closedAction, enabled]);
}

/** Portal home / project tab engagement. */
export function usePortalSession(
  portalId: string | null | undefined,
  source: 'client' | 'employee' | 'stakeholder' = 'client',
  projectId?: string | null,
  enabled = true,
) {
  useEngagementSession({
    portalId,
    projectId,
    source,
    page: 'portal',
    openedAction: 'portal_opened',
    closedAction: 'portal_closed',
    enabled,
  });
}

/** Proposal page engagement (when opened from a client portal). */
export function useProposalEngagementSession(
  portalId: string | null | undefined,
  projectId: string | null | undefined,
  source: 'client' | 'employee' | 'stakeholder',
  enabled: boolean,
  proposalSlug?: string,
) {
  useEngagementSession({
    portalId,
    projectId,
    source,
    page: 'proposal',
    openedAction: 'proposal_opened',
    closedAction: 'proposal_left',
    enabled,
    extraMetadata: proposalSlug ? { proposalSlug } : undefined,
  });
}

/** Portal PDF markup viewer — tracks time on document plus open/close. */
export function usePortalPdfEngagementSession(
  portalId: string | null | undefined,
  projectId: string | null | undefined,
  source: 'client' | 'employee' | 'stakeholder',
  enabled: boolean,
  metadata?: Record<string, unknown>,
) {
  useEngagementSession({
    portalId,
    projectId,
    source,
    page: 'portal_pdf',
    openedAction: 'portal_pdf_opened',
    closedAction: 'portal_pdf_closed',
    enabled,
    extraMetadata: metadata,
  });
}

/** Embedded 3D model review (approval walkthrough or inline viewer). */
export function usePortal3dModelEngagementSession(
  portalId: string | null | undefined,
  projectId: string | null | undefined,
  source: 'client' | 'employee' | 'stakeholder',
  enabled: boolean,
  metadata?: Record<string, unknown>,
) {
  useEngagementSession({
    portalId,
    projectId,
    source,
    page: 'portal_3d_model',
    openedAction: 'portal_3d_model_opened',
    closedAction: 'portal_3d_model_closed',
    enabled,
    extraMetadata: metadata,
  });
}

/** Full-page portal inspiration board (/portal/:slug/board). */
export function usePortalInspirationBoardEngagementSession(
  portalId: string | null | undefined,
  projectId: string | null | undefined,
  source: 'client' | 'employee' | 'stakeholder',
  enabled: boolean,
  metadata?: Record<string, unknown>,
) {
  useEngagementSession({
    portalId,
    projectId,
    source,
    page: 'portal_inspiration_board',
    openedAction: 'portal_inspiration_board_opened',
    closedAction: 'portal_inspiration_board_closed',
    enabled,
    extraMetadata: metadata,
  });
}

const EXTERNAL_3D_CLICK_WINDOW_MS = 60_000;
const EXTERNAL_3D_MAX_SESSION_MS = 2 * 60 * 60_000;

type External3dTabSession = {
  portalId: string;
  projectId: string | null;
  source: 'client' | 'employee' | 'stakeholder';
  metadata: Record<string, unknown>;
  clickedAt: number;
  awaySince: number | null;
  accumulatedMs: number;
  closed: boolean;
  timeoutId: ReturnType<typeof setTimeout> | null;
};

let external3dTabSession: External3dTabSession | null = null;
let external3dListenersReady = false;

async function finalizeExternal3dTabSession(endReason?: string) {
  const session = external3dTabSession;
  if (!session || session.closed) return;
  session.closed = true;
  if (session.timeoutId) clearTimeout(session.timeoutId);

  if (session.awaySince !== null) {
    session.accumulatedMs += Date.now() - session.awaySince;
    session.awaySince = null;
  }

  const secondsOnPage = Math.round(session.accumulatedMs / 1000);
  await logPortalActivity(
    session.portalId,
    session.projectId,
    'page_view',
    'portal_3d_model_closed',
    {
      page: 'portal_3d_model',
      secondsOnPage,
      context: 'deliverables_table_external',
      ...(endReason ? { endReason } : {}),
      ...session.metadata,
    },
    session.source,
  );
  external3dTabSession = null;
}

function onExternal3dVisibilityChange() {
  const session = external3dTabSession;
  if (!session || session.closed) return;

  if (document.visibilityState === 'hidden') {
    const withinClickWindow = Date.now() - session.clickedAt <= EXTERNAL_3D_CLICK_WINDOW_MS;
    const alreadyTracking = session.awaySince !== null || session.accumulatedMs > 0;
    if ((withinClickWindow || alreadyTracking) && session.awaySince === null) {
      session.awaySince = Date.now();
    }
    return;
  }

  if (document.visibilityState === 'visible' && session.awaySince !== null) {
    session.accumulatedMs += Date.now() - session.awaySince;
    session.awaySince = null;
    void finalizeExternal3dTabSession('returned_to_portal');
  }
}

function ensureExternal3dListeners() {
  if (external3dListenersReady) return;
  external3dListenersReady = true;
  document.addEventListener('visibilitychange', onExternal3dVisibilityChange);
  window.addEventListener('beforeunload', () => {
    void finalizeExternal3dTabSession('unload');
  });
}

/**
 * Tracks 3D model review opened in a new tab from the deliverables table.
 * Logs open on click, measures time while the portal tab is in the background,
 * and logs close when the client returns (or on tab close / timeout).
 */
export function beginPortal3dModelExternalTabSession(
  portalId: string,
  projectId: string | null | undefined,
  source: 'client' | 'employee' | 'stakeholder',
  metadata?: Record<string, unknown>,
) {
  void finalizeExternal3dTabSession('superseded');

  const session: External3dTabSession = {
    portalId,
    projectId: projectId ?? null,
    source,
    metadata: metadata ?? {},
    clickedAt: Date.now(),
    awaySince: null,
    accumulatedMs: 0,
    closed: false,
    timeoutId: setTimeout(
      () => void finalizeExternal3dTabSession('timeout'),
      EXTERNAL_3D_MAX_SESSION_MS,
    ),
  };
  external3dTabSession = session;
  ensureExternal3dListeners();

  void logPortalActivity(
    portalId,
    projectId,
    'page_view',
    'portal_3d_model_opened',
    {
      page: 'portal_3d_model',
      context: 'deliverables_table_external',
      ...metadata,
    },
    source,
  );
}
